import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, CircleDollarSign, MoveRight } from "lucide-react";
import { AcDialog, Button, Icon, Rows, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import {
  containerSpan,
  dayOf,
  dowShortDate,
  formatTime12,
  dowOf,
  groupByYear,
  offDeparture,
  offRoute,
  tourSummary,
  yearSummary,
  type OffItem,
  type ShowItem,
  type TimelineItem,
  type TourItem,
  type YearGroup,
} from "./lib/bookingRows.js";
import {
  pnlTone,
  signedUsdCents,
  tourPnlCents,
  tourTotalCents,
  usdCents,
  yearPnlCents,
  type MoneyByShow,
} from "./lib/bookingMoney.js";
import { useSettlement, useUpdateSettlement, useShowTotalBreakdown } from "../../app/hooks/useMoney.js";
import { markDepositReceivedBody } from "./lib/settlementBody.js";
import { useMediaObjectUrl, useSharedFlyerUrl } from "../../app/hooks/useMedia.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { AcMark } from "./AcMark.js";
import styles from "./BookingRows.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];
type ShowMoneyRow = components["schemas"]["ShowMoneyRow"];

/**
 * The band roster indexed by person id — built ONCE by the screen and handed down, so a page of rows resolves
 * every member face from one query instead of one per row. `ShowSummary` carries person ids precisely so this
 * join happens on the client against data it already holds.
 */
export type TeamById = ReadonlyMap<string, { displayName?: string | null; avatarMediaId?: string | null }>;
/** `showId → { date, venue }`, for an off/travel row's "Linked to {show}" line. Built ONCE by the screen from
 *  the shows it already loaded — the day carries only the linked show's id (ART-240). */
export type ShowRefById = ReadonlyMap<string, { date?: string; venueName?: string | null }>;
type TourDayDetail = components["schemas"]["TourDayDetail"];


export interface BookingTimelineProps {
  items: TimelineItem[];
  onOpenShow: (showId: string) => void;
  /**
   * Open the read-only SHARED-show detail — the row action for an accepted friend-band share (`item.shared`).
   * A shared row is an ordinary-looking show row, but the guest has no `/shows/{id}` access, so it opens the
   * `/shared-shows/{id}` matrix instead of the band's own preview/editor (ART-165).
   */
  onOpenShared?: (showId: string) => void;
  /**
   * HOLDS ONLY: when supplied, every show row swaps its trailing `View` for `Edit` and opens the show EDITOR
   * instead of the preview (PO ruling 2026-08-23, TASK-057). A hold is a date still being negotiated, so the
   * useful verb is edit — confirm it, move it, or drop it — not "open a settlement workbench" for a night that
   * may never happen. Absent on Confirmed/Past, which keep `View`.
   */
  onEditShow?: (showId: string) => void;
  /** Open a show in the editor regardless of the row's own action — the Total breakdown's "Edit in show
   *  editor →" always edits, even on Confirmed rows (whose row action is View). Undefined ⇒ read-only seat. */
  onEditInEditor?: (showId: string) => void;
  /** Clicking an off/travel row edits it — the row IS the edit affordance (there is no View). */
  onEditOffDay: (day: TourDayDetail) => void;
  /** Past only: wrap the timeline in the spec's year accordion (newest year first, newest year open). */
  byYear?: boolean;
  /**
   * The window's money, keyed by `showId` — from the SEPARATE money-gated `/shows/money` list. Absent for a
   * money-free seat, and every figure below is then simply not rendered: the row shape does not change, so
   * the two seats read the same list with one carrying numbers.
   */
  money?: MoneyByShow;
  /**
   * The band roster, indexed by person id — loaded ONCE by the screen. The redesigned row's foot shows member
   * faces, and resolving them per row would be an N+1 over a roster the page already has.
   */
  team?: TeamById;
  /** `showId → {date, venue}` for the "Linked to {show}" line on off/travel rows (ART-240). */
  showsById?: ShowRefById;
}

/**
 * The Booking shows list: one merged timeline of tour groups, show rows and recessed off/travel rows —
 * flat on the upcoming tabs, inside the year accordion on Past.
 *
 * The ROWS are money-free by construction — they are built from `ShowSummary`, which carries no money and is
 * proved money-free by a server-side reflection test. The reference's per-row `Guarantee`, the tour header's
 * total, the Past tab's four-stat cluster and the `· P&L` half of a year header come from the SEPARATE
 * money-gated `/shows/money` list, handed in as `money` and looked up by `showId`. That keeps the invariant
 * (money is separate endpoints/DTOs, never a conditional field on a shared shape) while the surface finally
 * matches the spec. Omit `money` and the identical list renders with no figures at all.
 */
export function BookingTimeline({ items, onOpenShow, onOpenShared, onEditShow, onEditInEditor, onEditOffDay, byYear, money, team, showsById }: BookingTimelineProps) {
  if (byYear) {
    const years = groupByYear(items);
    return (
      <div className={styles.years}>
        {years.map((group, i) => (
          // Newest year OPEN, the rest collapsed — the spec's rule, and it keeps a decade of history from
          // painting as one endless list.
          <details key={group.year} className={styles.year} open={i === 0}>
            <summary className={styles.yearHead}>
              <Icon icon={ChevronDown} size={16} className={styles.yearChevron} />
              <span className={styles.yearLabel}>{group.year}</span>
              {/* The reference's header is `{N} shows · P&L +$X` — the count money-free, the P&L a suffix. */}
              <span className={styles.yearMeta}>
                {yearSummary(group)}
                {money && <YearPnl group={group} money={money} />}
              </span>
            </summary>
            <Rows>
              {group.items.map((item) =>
                // ART-240 §02 — a past SHOW wraps its attributed off/travel days in a closable RUN CONTAINER
                // (the SAME component the upcoming tabs use); tours keep their own grouping.
                item.kind === "show" ? (
                  <ShowContainer
                    key={item.key}
                    item={item}
                    money={money}
                    team={team}
                    onOpenShow={onOpenShow}
                    // A PAST accepted friend-band share renders here too (ART-165 + the guest-Past fix), and it
                    // opens the read-only `/shared-shows/{id}` detail — the guest has no `/shows/{id}` access.
                    // Without this the row's click hit `onOpenShared?.()` as undefined and did nothing.
                    onOpenShared={onOpenShared}
                    onEditInEditor={onEditInEditor}
                    onEditOffDay={onEditOffDay}
                    past
                  />
                ) : (
                  <TimelineRow
                    key={item.key}
                    item={item}
                    onOpenShow={onOpenShow}
                    onOpenShared={onOpenShared}
                    onEditInEditor={onEditInEditor}
                    onEditOffDay={onEditOffDay}
                    money={money}
                    team={team}
                    showsById={showsById}
                    past
                  />
                ),
              )}
            </Rows>
          </details>
        ))}
      </div>
    );
  }

  return (
    <Rows>
      {items.map((item) => (
        <TimelineRow
          key={item.key}
          item={item}
          onOpenShow={onOpenShow}
          onOpenShared={onOpenShared}
          onEditShow={onEditShow}
          onEditInEditor={onEditInEditor}
          onEditOffDay={onEditOffDay}
          money={money}
          team={team}
          showsById={showsById}
        />
      ))}
    </Rows>
  );
}

/** The year header's `· P&L +$X`. It sums each show's YEAR figure, which includes flights + per diem while
 * the rows beneath exclude them — so this total will NOT equal the rows added up. That is the documented
 * legacy quirk, and the spec's standing instruction is to disclose it rather than silently "fix" it, which
 * is what the title here does. */
function YearPnl({ group, money }: { group: YearGroup; money: MoneyByShow }) {
  const cents = yearPnlCents(group, money);
  // The disclosure rides BOTH the accessible name and the tooltip from one string: `title` alone is
  // pointer-only (no keyboard, nothing on touch) and screen readers treat it inconsistently, so on its own
  // it would not actually be "documented in the UI" for most of the people reading this figure.
  const note = "includes flights and per diem, so it is not the sum of the rows below";
  return (
    <>
      {" · P&L "}
      <span
        className={styles.pnl}
        data-tone={pnlTone(cents)}
        title={`P&L ${note}.`}
        aria-label={`${group.year} P&L ${signedUsdCents(cents)} — ${note}.`}
      >
        {signedUsdCents(cents)}
      </span>
    </>
  );
}

/** One timeline entry — tour group, off/travel day or show — so the flat list and each year render the same. */
function TimelineRow({
  item,
  onOpenShow,
  onOpenShared,
  onEditShow,
  onEditInEditor,
  onEditOffDay,
  money,
  past,
  team,
  showsById,
}: {
  item: TimelineItem;
  onOpenShow: (showId: string) => void;
  onOpenShared?: (showId: string) => void;
  onEditShow?: (showId: string) => void;
  onEditInEditor?: (showId: string) => void;
  onEditOffDay: (day: TourDayDetail) => void;
  money?: MoneyByShow;
  past?: boolean;
  team?: TeamById;
  showsById?: ShowRefById;
}) {
  return item.kind === "tour" ? (
    <TourGroup
      group={item}
      onOpenShow={onOpenShow}
      onEditShow={onEditShow}
      onEditInEditor={onEditInEditor}
      onEditOffDay={onEditOffDay}
      money={money}
      showsById={showsById}
      past={past}
      team={team}
    />
  ) : item.kind === "off" ? (
    <OffRow item={item} onEdit={onEditOffDay} team={team} showsById={showsById} />
  ) : (
    // ART-240 — a loose show with attributed off/travel days renders as a RUN CONTAINER (show + its days);
    // one with none renders as a bare hero. The container decides which.
    <ShowContainer
      item={item}
      onOpenShow={onOpenShow}
      onOpenShared={onOpenShared}
      onEditShow={onEditShow}
      onEditInEditor={onEditInEditor}
      onEditOffDay={onEditOffDay}
      money={money}
      past={past}
      team={team}
    />
  );
}

/** The 52px date block that opens every row — mono day-of-week over the display-face day number. */
function DateBlock({ date, muted }: { date: string; muted?: boolean }) {
  return (
    <span className={styles.dateBlock} data-muted={muted || undefined}>
      <span className={styles.dow}>{dowOf(date)}</span>
      <span className={styles.day}>{dayOf(date)}</span>
    </span>
  );
}

function placeOf(show: ShowSummary): string {
  return [show.city, show.state].filter((p) => (p ?? "").trim() !== "").join(", ");
}

/**
 * The split-hero show row — `AC Upcoming Shows Redesign` (Aug 25 2026), replacing the flat `Row` line on the
 * Confirmed and Holds tabs.
 *
 * `220px 1fr`: poster left with the date badge on it, body right — bill line + status, a facts strip in two
 * clusters, then a foot of member faces and the actions. Past keeps `PastShowRow` (the reference's past rows
 * are a different anatomy with no flyer) and off/travel keeps `OffRow` ("no flyer, no guarantee, no View").
 *
 * TWO spec features render structurally but stay dark until their data exists — deliberately built now so
 * they light up without touching this file again. See `.design-specs/upcoming-shows.audit.md`:
 *   • the AC mark per act — `ShowBillAct.onArtistCntrl` is `friend_band_id != null`, populated at pick time
 *     for any registered band the lineup picker resolved (§3; broadened past friends by ART-165), so it lights
 *     for every act that is on Artist CNTRL and stays dark for free text / vendor acts;
 *   • the AC mark on the VENUE — needs the Venue CNTRL bridge we never ported (§4), deferred by the PO, so
 *     it is not rendered at all rather than hard-coded false;
 *   • `Set` — omitted. The spec shows one set time, but `SetTime` is per LINEUP SLOT and nothing identifies
 *     which slot is US, so there is no honest value to print (§8). Silent beats wrong.
 */
export function ShowRow({
  item,
  onOpenShow,
  onOpenShared,
  onEditShow,
  onEditInEditor,
  money,
  team,
}: {
  item: ShowItem;
  onOpenShow: (showId: string) => void;
  onOpenShared?: (showId: string) => void;
  onEditShow?: (showId: string) => void;
  /** Open a show in the editor regardless of the row's own action — the Total breakdown's "Edit in show
   *  editor →" (Confirmed rows open View, but the breakdown always edits). Undefined for a read-only seat. */
  onEditInEditor?: (showId: string) => void;
  money?: MoneyByShow;
  past?: boolean;
  /** The band roster, loaded ONCE for the page — the foot resolves member faces against it. */
  team?: TeamById;
}) {
  const show = item.show;
  const showId = show.showId!;
  const status = (show.status ?? "").trim();
  const figures = money?.get(showId);

  // ART-240 §02 (PO, 2026-09-26) — the Past tab now uses the SAME hero as Confirmed, wrapped by
  // `PastShowContainer` when a show has linked days. The old compact four-stat `PastShowRow` was discarded
  // with that ruling.

  // A shared (friend-band) show opens the GUEST EDITOR over `/shared-shows/{id}` (ART-165) — the inviter's
  // basics/lineup/run-of-show read-only, the guest's own sections editable for a `schedule:write` member and
  // read-only otherwise (gated inside the editor). The guest never reaches the band's own preview/editor.
  // Everything else about the row is identical, so it reads as a normal show.
  const open = () =>
    item.shared ? onOpenShared?.(showId) : onEditShow ? onEditShow(showId) : onOpenShow(showId);
  const bill = show.bill ?? [];
  const place = placeOf(show);

  return (
    <li className={`${styles.timelineCard} ${styles.hero}`}>
      <FlyerPanel mediaId={show.flyerMediaId} showId={showId} shared={item.shared} date={item.date} onOpen={open} />

      <div className={styles.heroBody}>
        <div className={styles.heroTop}>
          {/* The title block OPENS the show, like the poster — the flat row this replaces was clickable
              anywhere, and losing that would make the row's own name inert. A real <button> so keyboard and
              AT get it; the facts strip and the foot stay OUTSIDE it, because the foot carries its own
              controls and a button inside a button is invalid (the `Row` primitive's `trailing` rule). */}
          <button type="button" className={styles.heroTitle} onClick={open}>
            {/* The bill line is the acts, falling back to the venue when a show has no lineup yet — the
                reference's primary line is the bill, and an empty one would leave the row unnamed. */}
            <p className={styles.billLine}>
              {bill.length > 0
                ? bill.map((act, i) => (
                    <span key={`${act.artistName}-${i}`}>
                      {i > 0 && <span className={styles.billSep}> · </span>}
                      {act.artistName}
                      {act.onArtistCntrl && <AcMark title={`${act.artistName} is on Artist CNTRL`} />}
                    </span>
                  ))
                : show.venueName || "Untitled show"}
            </p>
            {/* The venue only repeats here when the BILL took the headline. With no lineup the venue IS the
                headline, so printing it again below would say the same thing twice. */}
            <p className={styles.venueLine}>
              {bill.length > 0
                ? [show.venueName || "Untitled show", place].filter(Boolean).join(" · ")
                : place}
            </p>
          </button>
          {status && <StatusBadge status={status} />}
        </div>

        <FactsStrip show={show} figures={figures} showId={showId} locked={figures?.settlementLocked}
          onEdit={onEditInEditor ? () => onEditInEditor(showId) : undefined} />

        <div className={styles.heroFoot}>
          <MemberFaces personIds={show.memberPersonIds ?? []} rsvps={show.memberRsvps ?? undefined} team={team} />
          <div className={styles.heroActions}>
            {show.ticketUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(show.ticketUrl!, "_blank", "noopener,noreferrer")}
              >
                Ticket link
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={open}
              aria-label={`${onEditShow ? "Edit" : "View"} ${show.venueName || "show"}`}
            >
              {onEditShow ? "Edit" : "View"}
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}

/**
 * The row's status badge — an INVERSE PAIR the shared `Tag` cannot express: every one of its tones is a wash
 * (`accent` is accent-chip fill with accent TEXT), while the spec calls for a solid crimson fill with white
 * text for Confirmed and its exact inverse — paper fill, ink text, ink ring — for Hold.
 *
 * Kept local rather than pushed into `Tag` because it is a bespoke pair for this row's design language, not a
 * capability the primitive is missing: adding a solid fill AND an outline treatment for one surface would
 * widen `Tag` for a single caller. Revisit if a second surface wants the same pair.
 */
function StatusBadge({ status }: { status: string }) {
  return (
    <span className={styles.heroStatus} data-status={status}>
      {status}
    </span>
  );
}

/** The 220px poster with the date badge on its top-left corner. Clicking it opens the show, like the row.
 *
 * A SHARED (friend-band) row's flyer bytes live in the HOST's tenant, so the ordinary band-scoped media route
 * 404s them for the guest — it fetches the guest-reachable shared-flyer route instead (ART-165). Both hooks run
 * unconditionally (the inactive one takes null inputs and resolves nothing); `art` picks the active one. */
function FlyerPanel({ mediaId, showId, shared, date, onOpen }: {
  mediaId?: string | null;
  showId: string;
  shared?: boolean;
  date: string;
  onOpen: () => void;
}) {
  const own = useMediaObjectUrl(shared ? null : mediaId, null, { variant: "sm" });
  const guest = useSharedFlyerUrl(shared ? showId : null, Boolean(shared && mediaId), "sm");
  const art = shared ? guest : own;
  return (
    <button type="button" className={styles.poster} onClick={onOpen} aria-label="Open show">
      {art.url ? (
        <img className={styles.posterImg} src={art.url} alt="" aria-hidden="true" loading="lazy" />
      ) : (
        // The reference stamps the empty ground with the word — at 220px it reads as a label on the poster
        // slot rather than a caption under it, which is why it is centred IN the panel and not below.
        <span className={styles.posterCaption} aria-hidden="true">Flyer</span>
      )}
      <span className={styles.posterDate}>
        <span className={styles.posterDow}>{dowOf(date)}</span>
        <span className={styles.posterDay}>{dayOf(date)}</span>
      </span>
    </button>
  );
}

/**
 * The facts strip — show OPS left, MONEY right behind a hairline.
 *
 * The money cluster is money-gated by CONSTRUCTION rather than by a capability check here: its figures ride
 * `MoneyByShow`, which only exists when the page's money query mounted, so a no-money seat renders the ops
 * cluster and nothing else. `Set` is absent by design — see `ShowRow`.
 *
 * Every part of it is optional, INCLUDING the strip itself: each rule here is drawn only when it has content
 * on both sides of it.
 */
function FactsStrip({
  show,
  figures,
  showId,
  locked,
  onEdit,
}: {
  show: ShowSummary;
  figures?: ShowMoneyRow;
  showId: string;
  /** Open THIS show in the editor — the breakdown popover's "Edit in show editor →". Undefined for a seat
   *  that cannot edit (no `schedule:write`), where the link is simply not shown. */
  onEdit?: () => void;
  /**
   * A SETTLED show refuses the settlement PUT (422 `settlement-locked`), so the chip is display-only there.
   * This is `settlementLocked`, never the row's `settled` — that one means "a settlement row exists", which
   * a deposit implies by construction (it is stored on that row), so gating on it made the chip inert on
   * every show it could ever appear on.
   */
  locked?: boolean;
}) {
  const doors = formatTime12(show.doorsTime);
  // Sold: the settlement's door headcount (ART-252 — the one source), off the money-gated money row. A show
  // with no worksheet renders `—`, never `0 / 250` — a fabricated zero reads as "sold none", a real claim.
  const sold = figures?.ticketsSold ?? null;
  const cap = show.capacity ?? null;
  const ops = doors != null || sold != null || cap != null;
  const money = figures?.guaranteeCents != null || figures?.depositCents != null;

  // A rule is a consequence of having two things to separate, never a fixed part of the row. With no facts at
  // all the strip was still an empty padded band with a hairline above AND below it, dividing nothing from
  // nothing — and that is the DEFAULT show, because doors, capacity and a guarantee are each optional.
  // Render no strip. (The sibling rule BETWEEN the clusters is the same bug one level down, and is dropped in
  // the stylesheet off `:first-child` — so the money cluster loses its leading hairline when it is alone.)
  if (!ops && !money) return null;

  return (
    <div className={styles.facts}>
      {ops && (
        <div className={styles.factGroup}>
          {doors && <Fact label="Doors" value={doors} />}
          {(sold != null || cap != null) && (
            <Fact label="Tickets" value={`${sold ?? "—"} / ${cap ?? "—"}`} />
          )}
        </div>
      )}
      {money && (
        <div className={`${styles.factGroup} ${styles.factGroupMoney}`}>
          {figures?.guaranteeCents != null && <Fact label="Guarantee" value={usdCents(figures.guaranteeCents)} />}
          {figures?.depositCents != null && (
            <Fact
              label="Deposit"
              value={usdCents(figures.depositCents)}
              badge={<DepositBadge showId={showId} figures={figures} locked={locked} />}
            />
          )}
          {/* ART-240 — the booking-list "Show Total": guarantee net of road costs + the attributed linked
              days' per-diem + pay. `Total ▾` opens the itemised breakdown (§01). Shown whenever there is a
              deal or any linked-day cost to net. */}
          {figures && (figures.guaranteeCents != null || (figures.linkedDaysCents ?? 0) > 0) && (
            <TotalFact showId={showId} figures={figures} onEdit={onEdit} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The deposit's PAID / UNPAID chip — and, while it is unpaid, the control that clears it.
 *
 * `depositCents` is what the deal AGREED; `depositReceivedOn` is whether the money turned up. Marking it
 * received is a money statement someone else reads as fact (it is what nets off the balance owed at the
 * door), so it goes behind an explicit confirm rather than firing on a stray click — the reason it is
 * `AcDialog` and never a hand-rolled dialog or `window.confirm`.
 *
 * NOT capability-gated here, deliberately: the chip only exists when `figures` do, and those ride the
 * money-gated `/shows/money` list whose gate (`money:full | money:per_show`) is the SAME pair the settlement
 * PUT requires. A second check would be a copy of that pair going stale on its own.
 *
 * The settlement document is fetched only once the dialog is OPEN. The PUT is a whole-document write so it
 * needs the record, but loading one per row would be an N+1 across a list that already holds every figure it
 * renders — and the confirm step is exactly the moment the document is first needed.
 */
function DepositBadge({
  showId,
  figures,
  locked,
}: {
  showId: string;
  figures: ShowMoneyRow;
  locked?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const settlement = useSettlement(showId, confirming);
  const update = useUpdateSettlement();

  const received = figures.depositReceivedOn != null;
  // A SETTLED settlement refuses the PUT, so offering the action there would be a button that can only ever
  // fail. Past rows are a different anatomy entirely and never reach this, but a settled show can still be
  // read on the Confirmed tab.
  //
  // `settlementLocked` (status = 'settled'), NOT the row's `settled` flag — which asks the OTHER question,
  // "does a settlement row exist", and is the P&L pivot. The deposit is stored ON that row, so `settled` is
  // true whenever this chip is drawn at all: gating here on it shipped a control that was never once
  // clickable, on any show, for anyone.
  const actionable = !received && !locked;

  if (!actionable) {
    return (
      <span className={styles.depositBadge} data-paid={received ? "" : undefined}>
        {received ? "Paid" : "Unpaid"}
      </span>
    );
  }

  const d = settlement.data?.data;

  return (
    <>
      <button
        type="button"
        className={styles.depositBadge}
        // The visible word is the STATUS; the accessible name has to be the ACTION, or the control announces
        // itself as "Unpaid, button" and never says what pressing it does.
        aria-label="Mark deposit as received"
        title="Mark deposit as received"
        onClick={() => setConfirming(true)}
      >
        Unpaid
      </button>
      {confirming && (
        <AcDialog
          title="Mark deposit as received?"
          message={`Are you sure you want to mark this ${usdCents(figures.depositCents ?? 0)} deposit as received? It will be stamped with today's date.`}
          confirmLabel="Mark as received"
          confirmIcon={CircleDollarSign}
          // Covers BOTH waits, and they are different: the settlement document still loading, and the PUT in
          // flight. Confirming before the document arrives would send a body built from nothing, so the
          // spinner is the honest state rather than a button that no-ops. It also disables Cancel for that
          // first moment (AcDialog disables the pair) — acceptable because Escape and the scrim are wired to
          // `onCancel` independently of `loading`, so the dialog is never a trap.
          loading={settlement.isPending || update.isPending}
          onConfirm={() => {
            if (!d) return;
            update.mutate(
              { showId, version: settlement.data?.version ?? 0, body: markDepositReceivedBody(d) },
              { onSuccess: () => setConfirming(false) },
            );
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}

function Fact({ label, value, badge, title }: { label: string; value: string; badge?: ReactNode; title?: string }) {
  return (
    <div className={styles.fact} title={title}>
      <span className={styles.factLabel}>{label}</span>
      {/* A DIV, not a span: the deposit badge brings a dialog with it, and a <div> inside a <span> is
          invalid nesting. The rule was already `display: flex`, so nothing moves. */}
      <div className={styles.factValue}>
        {value}
        {badge}
      </div>
    </div>
  );
}

type Breakdown = NonNullable<components["schemas"]["ShowTotalBreakdown"]>;

/**
 * The §01 Show-Total breakdown popover, shared by the row's `Total ▾` fact and the run container's header
 * `TOTAL ▾`. It PORTALS to the body because both live inside a card that clips its overflow (the `Picker`
 * situation), anchors under its trigger, fetches LAZILY (only while open), and dismisses on outside-click or
 * Escape. The total is the authority (`showTotalCents`) and the server guarantees the itemised lines sum to
 * it, so the popover cannot disagree with the collapsed value.
 *
 * Split into a hook (state + anchor + dismiss, shared) and a portal component (the lazy fetch, so `useQuery`
 * mounts only while open) so the two triggers cannot drift on any of that behaviour.
 */
function useBreakdownPopover() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    };
    reposition();
    // The popover is `position: fixed`, so a static anchor STRANDS the moment the list scrolls — it holds a
    // viewport coordinate while the trigger moves away. Recompute from the live trigger rect on every scroll
    // (capture-phase, so it catches a scroll in ANY ancestor scroll container, not just the window) and on
    // resize, so it stays glued under its anchor.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Dismiss: outside mousedown (excluding the trigger AND the portaled popover — so clicking the trigger only
  // toggles, never close-then-reopen) + capture-phase Escape (closes this layer only).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return { open, setOpen, anchor, triggerRef, popRef };
}

/** The portaled breakdown card — mounted only while open, so its query is lazy by construction. */
function BreakdownPortal({
  showId,
  anchor,
  popRef,
  onEdit,
}: {
  showId: string;
  anchor: { top: number; right: number };
  popRef: React.RefObject<HTMLDivElement | null>;
  onEdit?: (() => void) | undefined;
}) {
  const breakdown = useShowTotalBreakdown(showId, true);
  return createPortal(
    <div
      ref={popRef}
      className={styles.breakdownPop}
      role="dialog"
      aria-label="Show total breakdown"
      style={{ position: "fixed", top: anchor.top, right: anchor.right }}
    >
      {breakdown.isLoading || !breakdown.data ? (
        <div className={styles.breakdownLoading}><Spinner size={18} /></div>
      ) : (
        <BreakdownBody data={breakdown.data} onEdit={onEdit} />
      )}
    </div>,
    document.body,
  );
}

/** The facts-strip Show-Total fact: `Total` label over the `$X ▾` trigger. */
function TotalFact({ showId, figures, onEdit }: { showId: string; figures: ShowMoneyRow; onEdit?: () => void }) {
  const pop = useBreakdownPopover();
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>Total</span>
      <button
        type="button"
        ref={pop.triggerRef}
        className={styles.totalTrigger}
        aria-expanded={pop.open}
        aria-label="Show total breakdown"
        onClick={() => pop.setOpen((o) => !o)}
      >
        {usdCents(figures.showTotalCents ?? 0)}
        <ChevronDown size={14} className={styles.totalChevron} data-open={pop.open || undefined} aria-hidden="true" />
      </button>
      {pop.open && pop.anchor && (
        <BreakdownPortal
          showId={showId}
          anchor={pop.anchor}
          popRef={pop.popRef}
          onEdit={onEdit && (() => { pop.setOpen(false); onEdit(); })}
        />
      )}
    </div>
  );
}

/** The run container header's `TOTAL ▾ $X` — the same breakdown, stacked (label + chevron over the value) and
 *  right-aligned to sit opposite the run title. */
function HeaderTotal({ showId, figures, onEdit }: { showId: string; figures: ShowMoneyRow; onEdit?: () => void }) {
  const pop = useBreakdownPopover();
  return (
    <div className={styles.runTotal}>
      <button
        type="button"
        ref={pop.triggerRef}
        className={styles.runTotalTrigger}
        aria-expanded={pop.open}
        aria-label="Show total breakdown"
        onClick={() => pop.setOpen((o) => !o)}
      >
        <span className={styles.runTotalTop}>
          Total
          <ChevronDown size={13} className={styles.runTotalChevron} data-open={pop.open || undefined} aria-hidden="true" />
        </span>
        <span className={styles.runTotalValue}>{usdCents(figures.showTotalCents ?? 0)}</span>
      </button>
      {pop.open && pop.anchor && (
        <BreakdownPortal
          showId={showId}
          anchor={pop.anchor}
          popRef={pop.popRef}
          onEdit={onEdit && (() => { pop.setOpen(false); onEdit(); })}
        />
      )}
    </div>
  );
}

/** The breakdown popover's body: INCOME lines, EXPENSES grouped (the show's own, then each linked day), then
 *  a foot with "Edit in show editor →" and the authoritative Total. */
function BreakdownBody({ data, onEdit }: { data: Breakdown; onEdit?: (() => void) | undefined }) {
  const income = data.income ?? [];
  const expenses = data.expenses ?? [];
  return (
    <>
      {income.length > 0 && (
        <div className={styles.bkSection}>
          <p className={styles.bkHead}>Income</p>
          {income.map((l, i) => (
            <div key={i} className={styles.bkLine}>
              <span className={styles.bkLabel}>{l.label}</span>
              <span className={styles.bkAmount}>{usdCents(l.amountCents ?? 0)}</span>
            </div>
          ))}
        </div>
      )}
      {expenses.length > 0 && (
        <div className={styles.bkSection}>
          <p className={styles.bkHead}>Expenses</p>
          {expenses.map((g, gi) => (
            <div key={gi} className={styles.bkGroup}>
              {g.dayDate && (
                <p className={styles.bkDayHead}>
                  {dowOf(g.dayDate).toUpperCase()} {dayOf(g.dayDate)} · {g.dayKind === "travel" ? "TRAVEL" : "DAY OFF"}
                </p>
              )}
              {(g.lines ?? []).map((l, li) => (
                <div key={li} className={styles.bkLine}>
                  <span className={styles.bkLabel}>{l.label}</span>
                  <span className={styles.bkAmountNeg}>−{usdCents(l.amountCents ?? 0)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className={styles.bkFoot}>
        {onEdit ? (
          <button type="button" className={styles.bkEdit} onClick={onEdit}>Edit in show editor →</button>
        ) : (
          <span />
        )}
        <span className={styles.bkTotalLabel}>Total</span>
        <span className={styles.bkTotalValue}>{usdCents(data.totalCents ?? 0)}</span>
      </div>
    </>
  );
}

/**
 * The foot's member faces — photo where the person has one, initials otherwise, `+N` past the cap. Each face
 * GLOWS by its RSVP (ART-84, PO: "I denied this show but I'm not seeing the glowing rings") — the show
 * preview's chip treatment (green in · amber pending · red out), so a pass reads from the list without opening
 * the show. `rsvps` is index-aligned with `personIds` (`ShowSummary.memberRsvps`); a face with no recognised
 * RSVP (an off/travel day's members carry none) draws no ring. Spaced, not overlapped: the foot has the room.
 *
 * Resolved against the band roster the PAGE loaded, not a fetch per row: `ShowSummary` carries person ids
 * only, precisely so the list does not re-derive names and photos it already holds (the `MembersLine`
 * precedent on the show preview).
 */
function MemberFaces({ personIds, rsvps, team }: {
  personIds: string[];
  rsvps?: readonly string[];
  team?: TeamById;
}) {
  if (personIds.length === 0) return null;
  const shown = personIds.slice(0, MEMBER_FACE_CAP);
  const overflow = personIds.length - shown.length;
  return (
    <span className={styles.faces} title="Members on this show">
      {shown.map((id, i) => {
        const seat = team?.get(id);
        // Never a raw uuid: someone on the show who is no longer a crew seat is still a person.
        const name = seat?.displayName ?? "Invited member";
        const rsvp = rsvpOf(rsvps?.[i]);
        return (
          <span key={id} className={styles.face} data-rsvp={rsvp} title={rsvp ? `${name} · ${RSVP_WORD[rsvp]}` : name}>
            <PersonAvatar
              personId={id}
              avatarMediaId={seat?.avatarMediaId}
              initials={initialsOf(name)}
              size="md"
              shape="circle"
            />
          </span>
        );
      })}
      {overflow > 0 && <span className={`${styles.face} ${styles.faceMore}`}>+{overflow}</span>}
    </span>
  );
}

type Rsvp = "in" | "pending" | "out";
const RSVP_WORD: Record<Rsvp, string> = { in: "in", pending: "invited", out: "passed" };
/** Only the three vocabulary values ring — anything else (or nothing) draws a plain face. */
function rsvpOf(raw: string | undefined): Rsvp | undefined {
  return raw === "in" || raw === "pending" || raw === "out" ? raw : undefined;
}

/** At most four faces before the row starts counting instead — the reference shows three plus a `+2`. */
const MEMBER_FACE_CAP = 4;

/** Initials for a face — first letters of the first two words, so "Keep Warm" reads `KW`. */
function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";
}

/**
 * ART-240 — a show grouped with its attributed off/travel days into a closable RUN CONTAINER (the screenshot's
 * shape): a header naming the show (weekday + short date · venue) over its date span, a money-gated `TOTAL ▾`
 * opposite it, and a body interleaving the day rows and the show hero BY DATE (a travel day the morning before
 * sits above the show; a day-off after sits below). Used on every tab.
 *
 * The days come from `item.days`, which `buildTimeline` attributes CLIENT-SIDE (direct show-link, or a
 * run-linked day's "next show" — the PO's rule) — so the grouping is money-free and needs no per-show fetch.
 * A show with no attributed days renders as a bare hero (no container). The header Total and the hero's own
 * `Total ▾` are the same authoritative figure; the breakdown (fetched lazily when either is opened) itemises
 * the days' costs.
 */
function ShowContainer({
  item,
  money,
  team,
  onOpenShow,
  onOpenShared,
  onEditShow,
  onEditInEditor,
  onEditOffDay,
  past,
}: {
  item: ShowItem;
  money?: MoneyByShow;
  team?: TeamById;
  onOpenShow: (showId: string) => void;
  onOpenShared?: (showId: string) => void;
  onEditShow?: (showId: string) => void;
  onEditInEditor?: (showId: string) => void;
  onEditOffDay?: (day: TourDayDetail) => void;
  past?: boolean;
}) {
  const show = item.show;
  const showId = show.showId!;
  const figures = money?.get(showId);
  const days = item.days ?? [];
  const [open, setOpen] = useState(true);

  const hero = (
    <ShowRow
      item={item}
      onOpenShow={onOpenShow}
      onOpenShared={onOpenShared}
      onEditShow={onEditShow}
      onEditInEditor={onEditInEditor}
      money={money}
      past={past}
      team={team}
    />
  );
  // No attributed days ⇒ a bare hero, not an empty container.
  if (days.length === 0) return hero;

  // The body reads chronologically: the hero and the day rows interleaved by date. A day inside the container
  // does not repeat its "Linked to {show}" line — the container header IS the link — so it gets no `showsById`.
  const body = [
    { date: item.date, node: hero },
    ...days.map((d) => ({
      date: d.date,
      node: onEditOffDay ? <OffRow key={d.key} item={d} onEdit={onEditOffDay} team={team} /> : null,
    })),
  ]
    .filter((b) => b.node != null)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const title = `${dowShortDate(item.date)}${show.venueName ? ` · ${show.venueName}` : ""}`;
  const { range, dayCount } = containerSpan(item);

  return (
    <li className={styles.runContainer}>
      <div className={styles.runHead}>
        <button
          type="button"
          className={styles.runToggle}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown size={16} className={styles.runChevron} data-open={open || undefined} aria-hidden="true" />
          <span className={styles.runTitleBlock}>
            <span className={styles.runTitle}>{title}</span>
            <span className={styles.runRange}>{range} · {dayCount} {dayCount === 1 ? "day" : "days"}</span>
          </span>
        </button>
        {figures?.showTotalCents != null && (
          <HeaderTotal
            showId={showId}
            figures={figures}
            onEdit={onEditInEditor ? () => onEditInEditor(showId) : undefined}
          />
        )}
      </div>
      {open && <ul className={styles.runBody}>{body.map((b) => b.node)}</ul>}
    </li>
  );
}

/**
 * A label-over-value figure — the row's `Guarantee`, a tour header's `Tour total`, and each of the four
 * stats on a past row. One block at two sizes rather than two near-identical local classes; `size="lg"` is
 * the figure that stands alone on a row, the default is one of four sharing the space.
 *
 * NOT `BookingKpiStrip`'s tile: that is a display-face hero figure carrying a lifetime sub-line, a different
 * contract rather than a third size of this one.
 */
function Figure({
  label,
  value,
  tone,
  size,
}: {
  label: string;
  value: string;
  tone?: "up" | "down";
  size?: "lg";
}) {
  return (
    <span className={styles.figure} data-size={size}>
      <span className={styles.figureLabel}>{label}</span>
      <span className={styles.figureValue} data-tone={tone}>
        {value}
      </span>
    </span>
  );
}

/** The off/travel row's sub-line: `Depart 9:00 AM · Bus call 8:30 AM`, or `offDeparture`'s fallback. */
function offSubLine(day: TourDayDetail): string {
  const parts: string[] = [];
  const depart = formatTime12(day.departTime);
  if (depart) parts.push(`Depart ${depart}`);
  const bus = formatTime12(day.busCall);
  if (bus) parts.push(`Bus call ${bus}`);
  return parts.length ? parts.join(" · ") : offDeparture(day);
}

/**
 * The recessed off/travel row (ART-240 §04). No flyer, no guarantee, no View — clicking it edits the day.
 * Anatomy: a muted date block, a TRAVEL/OFF chip, the route (or the day-off city) as the headline over a
 * depart/bus-call sub-line, then the linked show, member faces and an Edit button.
 */
export function OffRow({
  item, onEdit, team, showsById,
}: {
  item: OffItem;
  onEdit: (day: TourDayDetail) => void;
  team?: TeamById;
  showsById?: ShowRefById;
}) {
  const day = item.day;
  const route = offRoute(day);
  const isTravel = day.kind === "travel";
  const headline = route
    ? "city" in route
      ? isTravel ? route.city : `Day off in ${route.city}`
      : null // rendered as a from→to route below
    : isTravel ? "Travel day" : "Day off";
  const personIds = (day.members ?? []).map((m) => m.personId).filter((id): id is string => !!id);
  const linked = day.showId ? showsById?.get(day.showId) : undefined;
  const linkedText = linked?.date
    ? `${dowOf(linked.date)} ${dayOf(linked.date)}${linked.venueName ? ` · ${linked.venueName}` : ""}`
    : undefined;

  const kindLabel = isTravel ? "Travel" : "Off";
  return (
    // A self-contained recessed CARD (ART-240 §04), like the show hero — its own border/radius and a
    // `margin-inline` gutter, so it never reads as a full-bleed band welded to its neighbours. The lead
    // (date + chip) is divided from the content by a hairline.
    <li className={`${styles.timelineCard} ${styles.offCard}`}>
      <button type="button" className={styles.offLead} onClick={() => onEdit(day)} aria-label={`Edit ${kindLabel.toLowerCase()} day`}>
        <DateBlock date={item.date} muted />
        <span className={styles.offChip} data-kind={isTravel ? "travel" : "off"}>{kindLabel}</span>
      </button>

      <div className={styles.offMain}>
        <button type="button" className={styles.offText} onClick={() => onEdit(day)}>
          {headline != null ? (
            <span className={styles.offHeadline}>{headline}</span>
          ) : route && !("city" in route) ? (
            <span className={styles.offHeadline}>
              <span className={styles.routeCity}>{route.from || "—"}</span>
              <Icon icon={MoveRight} size={16} className={styles.routeArrow} />
              <span className={styles.routeCity}>{route.to || "—"}</span>
            </span>
          ) : null}
          <span className={styles.offSub}>{offSubLine(day)}</span>
        </button>

        <div className={styles.offTrailing}>
          {linkedText && (
            <span className={styles.linkedTo}>
              <span className={styles.linkedToLabel}>Linked to</span>
              <span className={styles.linkedToValue}>{linkedText}</span>
            </span>
          )}
          <MemberFaces personIds={personIds} team={team} />
          <Button variant="ghost" size="sm" onClick={() => onEdit(day)}>Edit</Button>
        </div>
      </div>
    </li>
  );
}

/**
 * A tour: a native `<details>` whose summary carries the name over `range · N dates · M off days · C confirmed`
 * and whose body interleaves the tour's shows and off days by date. Open by default so a tour never hides the
 * dates the list exists to show.
 */
export function TourGroup({
  group,
  onOpenShow,
  onEditShow,
  onEditInEditor,
  onEditOffDay,
  money,
  past,
  team,
  showsById,
}: {
  group: TourItem;
  onOpenShow: (showId: string) => void;
  onEditShow?: (showId: string) => void;
  onEditInEditor?: (showId: string) => void;
  onEditOffDay: (day: TourDayDetail) => void;
  money?: MoneyByShow;
  past?: boolean;
  team?: TeamById;
  showsById?: ShowRefById;
}) {
  // Upcoming: `Tour total $X` (summed guarantee). Past: a `+$X` P&L pill instead, summed over the dates' ROW
  // figures — a third rule in one accordion, and genuinely what the legacy page does.
  const total = money && !past ? tourTotalCents(group, money) : null;
  const pnl = money && past ? tourPnlCents(group, money) : null;
  return (
    <li className={styles.tourItem}>
      <details className={styles.tour} open>
        <summary className={styles.tourHead}>
          <Icon icon={ChevronDown} size={16} className={styles.tourChevron} />
          <span className={styles.tourText}>
            <span className={styles.tourName}>{group.name}</span>
            <span className={styles.tourMeta}>{tourSummary(group)}</span>
          </span>
          {total != null && <Figure label="Tour total" value={usdCents(total)} size="lg" />}
          {pnl != null && (
            <span className={styles.pnl} data-tone={pnlTone(pnl)}>
              {signedUsdCents(pnl)}
            </span>
          )}
        </summary>
        <div className={styles.tourBody}>
          <Rows>
            {group.children.map((child) =>
              child.kind === "off" ? (
                <OffRow key={child.key} item={child} onEdit={onEditOffDay} team={team} showsById={showsById} />
              ) : (
                <ShowRow
                  key={child.key}
                  item={child}
                  onOpenShow={onOpenShow}
                  onEditShow={onEditShow}
                  onEditInEditor={onEditInEditor}
                  money={money}
                  past={past}
                  team={team}
                />
              ),
            )}
          </Rows>
        </div>
      </details>
    </li>
  );
}
